import * as turf from '@turf/turf'
import type Checkpoint from '../model/Checkpoint'

/** GeoJSON order: [longitude, latitude] */
export type RouteCoordinate = [number, number]

export function getRouteCoordinates(checkpoints: Checkpoint[]): RouteCoordinate[] {
  return [...checkpoints]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((cp) => cp.lat != null && cp.lng != null)
    .map((cp) => [cp.lng!, cp.lat!] as RouteCoordinate)
}

export function getProgressPercentage(progress: number, totalDistance: number): number {
  if (totalDistance <= 0) return 0
  if (progress >= totalDistance) return 100
  return (progress / totalDistance) * 100
}

export function sampleRoutePoints(
  routeCoordinates: RouteCoordinate[],
  count = 120,
): RouteCoordinate[] {
  if (routeCoordinates.length < 2) return routeCoordinates
  const line = turf.lineString(routeCoordinates)
  const lengthKm = turf.length(line, { units: 'kilometers' })
  if (lengthKm <= 0) return routeCoordinates

  const samples: RouteCoordinate[] = []
  for (let i = 0; i <= count; i++) {
    const along = turf.along(line, (i / count) * lengthKm, { units: 'kilometers' })
    samples.push(along.geometry.coordinates as RouteCoordinate)
  }
  return samples
}

export function projectRouteToSvg(
  coordinates: RouteCoordinate[],
  width: number,
  height: number,
  padding = 12,
): { x: number; y: number }[] {
  if (!coordinates.length) return []

  const collection = turf.featureCollection(coordinates.map((coord) => turf.point(coord)))
  const bbox = turf.bbox(collection)
  const spanLng = Math.max(bbox[2]! - bbox[0]!, 0.0001)
  const spanLat = Math.max(bbox[3]! - bbox[1]!, 0.0001)
  const innerW = width - padding * 2
  const innerH = height - padding * 2
  const scale = Math.min(innerW / spanLng, innerH / spanLat)

  return coordinates.map(([lng, lat]) => {
    const x = padding + (lng - bbox[0]!) * scale
    const y = padding + (bbox[3]! - lat) * scale
    return { x, y }
  })
}
